import type { JSX } from 'react';

import { Spinner } from '@change/ds-core-ui';

import { Flex } from '@change-corgi/design-system-legacy/layout';

import { SAP_L10N_MAP } from 'config/routes-l10n';

import { withPrefetchedData } from 'src/shared/prefetch';

import { prefetchPageContext, StartAPetitionPageContextProvider } from 'src/app/pages/startAPetition/pageContext';
import type { StartAPetitionPagePrefetchedDataProps } from 'src/app/pages/startAPetition/pageContext';

import { StartAPetitionLayout } from '../layout';

import { StartAPetitionErrorHandler } from './ErrorHandler';
import { MutationObserverContainer } from './MutationObserverContainer';

function resolveLocale(locale: string, slug: string) {
	return locale.replace(
		/^([a-z]{2})(-[a-z]{2})?$/i,
		(_, g1: string, g2: string) => `/${g1.toLowerCase()}${g2 ? g2.toUpperCase() : ''}/${encodeURIComponent(slug)}`,
	);
}

export const StartAPetition = withPrefetchedData(
	({ prefetchedData }: StartAPetitionPagePrefetchedDataProps): JSX.Element => {
		return (
			<MutationObserverContainer>
				<StartAPetitionPageContextProvider
					prefetchedData={prefetchedData}
					errorHandler={StartAPetitionErrorHandler}
					loadingFallback={
						<Flex
							sx={{
								alignItems: 'center',
								justifyContent: 'center',
								height: ['calc(100vh - 48px)', 'calc(100vh - 58px)'],
								width: '100%',
							}}
						>
							<Spinner data-testid="loading-spinner" />
						</Flex>
					}
				>
					<StartAPetitionLayout />
				</StartAPetitionPageContextProvider>
			</MutationObserverContainer>
		);
	},
	{
		prefetchName: __MODULE_HASH__,
		prefetchData: async (context) => prefetchPageContext(context),
		pageTitle: ({ i18n, fromTemplate }) => fromTemplate(i18n.translate('corgi.sap.meta_tags.title')),
		pageMetadata: ({ i18n }) => ({
			description: i18n.translate('corgi.sap.meta_tags.description'),
		}),
		pageCanonical: ({ params, pathname }) => {
			const locale = params.locale || pathname.split('/')[1];
			if (locale) {
				const validL10n = SAP_L10N_MAP[locale.toLowerCase()];
				if (validL10n) {
					return resolveLocale(locale, validL10n.sap);
				}
			}
			return '/start-a-petition';
		},
	},
);
